<template>
  <div class="markowitz-chart-root">
    <div :class="[{ open: showSuggestionForm }, 'chart-header']">
      <div class="flex align-center">
        <horizontal-title-tooltip-section
          :title="$t('portfolioManager.markowitzChartTitle')"
          :tooltip="$t('portfolioManager.hints.markowitzOptimization')"
          size="m"
          spacing="left"
        />
        <p
          class="frontier-loader"
          v-if="isCalculatingFrontier || isCalculatingSuggestion"
        >
          <b-spinner small />
          <span v-if="isCalculatingFrontier">{{
            $t("portfolioManager.frontierIsBeingCalculated")
          }}</span>
          <span v-if="isCalculatingSuggestion">{{
            $t("portfolioManager.suggestionCalculated")
          }}</span>
        </p>
      </div>
      <div
        v-if="portfolio !== null && portfolio.portfolio_output"
        class="suggestion-form"
      >
        <b-button
          variant="primary"
          v-if="!showSuggestionForm"
          @click="showSuggestionForm = !showSuggestionForm"
        >
          <change-icon />
          {{
            $t("portfolioManager.suggestOptimalization.suggestOptimalization")
          }}
        </b-button>
        <div v-else><suggest-optimization-form /></div>
        <tooltip-info
          :title="$t('portfolioManager.suggestOptimalization.tooltip')"
          :class="[{ 'margin-bottom': showSuggestionForm }, 'tooltip-info']"
        />
      </div>
    </div>
    <div class="chart-with-sides">
      <div class="chart-left">
        <horizontal-title-tooltip-section
          :title="$t('portfolioManager.significantMeasures.expectedReturn')"
          :tooltip="$t('portfolioManager.hints.expectedReturn')"
        />
      </div>
      <v-chart
        class="markowitz-chart"
        :options="chartOptions"
        autoresize
        @click="handleChartClick"
      />
      <div class="chart-right">
        <div class="legend">
          <p class="body-bold frontier-expl" v-if="recalculatedPortfolio">
            <span
              :class="[
                { pool: !recalculatedPortfolio.custom_portfolio_data },
                'markline repl',
              ]"
            ></span>
            {{
              recalculatedPortfolio.custom_portfolio_data
                ? $t("portfolioManager.replacementFrontier")
                : $t("portfolioManager.replacementFrontierPool")
            }}
          </p>
          <p class="body-bold frontier-expl">
            <span class="markline"></span>
            <span v-if="selectedPool.pool_id === -1">{{
              $t("portfolioManager.poolOptions.sp100")
            }}</span>
            <span v-else-if="selectedPool.pool_id === -3">{{ selectedPool.name }}</span>
            <span v-else>
              {{$t("portfolioManager.poolOptions.fallback")}}
            </span>
          </p>
          <div class="legend-items">
            <legend-item
              v-for="(legendItem, i) in legendItems"
              v-bind="legendItem"
              v-bind:key="i"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="chart-footer">
      <horizontal-title-tooltip-section
        :title="$t('portfolioManager.significantMeasures.volatility')"
        :tooltip="$t('portfolioManager.hints.volatility')"
      />
      <div class="info-container" v-if="recalculatedPortfolio">
        <span
          class="info blue"
          v-if="recalculatedPortfolio.custom_portfolio_data"
          v-html="
            $t('portfolioManager.replacementFrontierHint', {
              stocks: this.recalculatedPortfolio.removedTickers.join(', '),
            })
          "
        />
        <span
          v-else
          class="info"
          v-html="$t('portfolioManager.replacementFrontierPoolHint')"
        />
      </div>
    </div>
    <div class="updated-at">
      <sync-icon width="10" height="10" />
      {{ updatedAt | formatDateTime }}
    </div>
    <point-detail v-if="pointDetail" />
  </div>
</template>

<script>
import {
  CUSTOM_PORTFOLIO,
  DARK_GREEN,
  ICONS_FILL_COLOR,
  LIGHT_GREEN,
  LIGHT_ORANGE,
  LIGHT_PURPLE,
  MAX_SHARPE,
  MIN_VOLATILITY,
  PRIMARY_FONT_BLUE,
  REPLACEMENT_MAX_SHARPE,
  REPLACEMENT_MIN_VOLATILITY,
  REPLACEMENT_PORTFOLIOS,
  WEIGHT_CHART_COLORS,
} from "../../utils/constants";
import {
  DataZoomComponent,
  GridComponent,
  MarkPointComponent,
  TooltipComponent,
} from "echarts/components";
import { LineChart, ScatterChart } from "echarts/charts";
import { filterObject, formatToPercents } from "../../utils/helper";
import { mapGetters, mapMutations } from "vuex";
import ChangeIcon from "vue-material-design-icons/SwapVerticalVariant.vue";
import ECharts from "vue-echarts";
import HorizontalTitleTooltipSection from "../inputs/HorizontalTitleTooltipSection.vue";
import LegendItem from "../LegendItem.vue";
import MainAlert from "../alerts/MainAlert.vue";
import PointDetail from "../PointDetail.vue";
import SuggestOptimizationForm from "../forms/SuggestOptimizationForm.vue";
import SyncIcon from "../../assets/img/icons/syncIcon.svg";
import TooltipInfo from "../TooltipInfo.vue";
import moment from "moment";
import { use } from "echarts/core";

use([
  GridComponent,
  LineChart,
  ScatterChart,
  TooltipComponent,
  MarkPointComponent,
  DataZoomComponent,
]);
export default {
  name: "markowitz-chart",
  components: {
    "v-chart": ECharts,
    HorizontalTitleTooltipSection,
    LegendItem,
    PointDetail,
    SyncIcon,
    ChangeIcon,
    SuggestOptimizationForm,
    TooltipInfo,
  },
  data() {
    return {
      showSuggestionForm: false,
      chartOptions: {
        grid: [
          {
            left: 50,
            width: "absolute",
            right: 25,
            show: true,
            borderWidth: 0,
            height: "auto",
            bottom: 25,
          },
        ],
        tooltip: {
          trigger: "item",
          axisPointer: "cross",
          extraCssText:
            "background: rgba(248, 249, 253, 0.91);box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);",
          formatter: (item) => {
            if (item.componentType === "markPoint") return null;
            return `
      <div class="chart-tooltip-inner">
        <p class="body-bold">${
          item.componentIndex === 7 ? item.data.label : item.seriesName
        }</p>
        <div class="chart-tooltip-item">
          <span class="name">
          ${this.$i18n.t("portfolioManager.significantMeasures.volatility")}:
          </span>
          <span class="value">
          ${this.formatToPercents(item.value[0], true)}
          </span>
        </div>
        <div class="chart-tooltip-item">
          <span class="name">
          ${this.$i18n.t(
            "portfolioManager.significantMeasures.expectedReturn"
          )}:
          </span>
          <span class="value">
          ${this.formatToPercents(item.value[1], true)}
          </span>
        </div>
      </div>
    `;
          },
        },
        dataZoom: [
          {
            type: "inside",
          },
        ],
        xAxis: {
          type: "value",
          max: (value) => (value.max + 0.1).toFixed(2),
          axisLabel: {
            formatter: (val) => Math.round(val * 10000) / 100 + " %",
          },
        },
        yAxis: {
          type: "value",
          max: (value) => (value.max + 0.1).toFixed(2),
          axisLabel: {
            formatter: (val) => Math.round(val * 10000) / 100 + " %",
          },
        },

        series: [
          {
            name: this.$i18n.t("portfolioManager.efficientFrontier"),
            data: [],
            type: "line",
            smooth: false,
            showSymbol: true,
            color: "#000000",
            symbolSize: 3,
            markPoint: {
              symbolSize: 8,
              name: "Selected point detail",
              data: [],
              symbol: "circle",
            },
          },
          {
            id: "replacementFrontier",
            name: this.$i18n.t("portfolioManager.replacementFrontier"),
            data: [],
            type: "line",
            smooth: true,
            showSymbol: true,
            symbolSize: 3,
            color: "#779EFF",
            markPoint: {
              symbolSize: 8,
              name: "Selected point detail",
              symbol: "circle",
              data: [],
            },
          },
          {
            id: CUSTOM_PORTFOLIO,
            name: this.$i18n.t("portfolioManager.yourPortfolio"),
            data: [],
            type: "scatter",
            color: ICONS_FILL_COLOR,
            symbolSize: 18,
          },
          {
            id: MIN_VOLATILITY,
            z: 3,
            name: "Minvar portfolio",
            data: [],
            type: "scatter",
            symbol:
              "image://data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAKCAYAAABmBXS+AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAD7SURBVHgBfZC9agJREIXPTJLdXGKSJU26kEJSyuYBEpIynSkkKVKYJ0iagKRKp5BCfALtVfARFu1svJWgjT/VViIorCDe0V3cQhAPzMBwzinmI2wl32knUHZWRBzQSp/l643Yo3Atft++cGL/UTLlwLIh4z4w8bXI8lUVGkMKcu+3OL8YHL98AInLuAyjmzCdpqfy1WcmMkW+f9gJhGL3EbDU0/wn47KAXLq6xj7xzR1wBJejyzrFITFBtIx6e03jj4AVNIuYkum2gfl0N9BpAbNpJfFf1RGCIJfJgqjMyVT0gPjjzQw9yPIzQhA3QxRCkg5hMoynCjUv9tZANFsJCpPkYAAAAABJRU5ErkJggg==",
          },
          {
            id: MAX_SHARPE,
            z: 3,
            name: "Max sharpe portfolio",
            data: [],
            type: "scatter",
            symbol:
              "image://data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEHSURBVHgBbZC9TsMwFIWPnZAyJFUCYigsXSqYkVirAA9CBiRWxAvQvkAVVkAiPAFdWFGEGHgEfhYW6EjUZGhN44ttqVH/zmDp+nw69j0MMzo/zUPOZQiJTE5kP06Cr6nHDBD9+taa/RBs8bCxzSEEMPiRKIay07v2uhV4cVbctXatqH1cq9KLnPDYH2GYycP41ku5Sms6DqL9A2f2F3A9hvaRA8ZwqWcO225ubHK4dYZFNXYsDYYGtIj82voytChelmU6+C4hxrRk6nsipAZUFWTjEV29vog5KFfLPD8JtS3d69nWB/1NOh9v5KuEk9aebdI/380r3d5NPanqwarCXZnEcZBNvX8KQWm+aVP7aAAAAABJRU5ErkJggg==",
          },
          {
            id: REPLACEMENT_MIN_VOLATILITY,
            z: 3,
            name: "Minvar portfolio",
            data: [],
            type: "scatter",
            symbol:
              "image://data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAKCAYAAABmBXS+AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAD7SURBVHgBfZC9agJREIXPTJLdXGKSJU26kEJSyuYBEpIynSkkKVKYJ0iagKRKp5BCfALtVfARFu1svJWgjT/VViIorCDe0V3cQhAPzMBwzinmI2wl32knUHZWRBzQSp/l643Yo3Atft++cGL/UTLlwLIh4z4w8bXI8lUVGkMKcu+3OL8YHL98AInLuAyjmzCdpqfy1WcmMkW+f9gJhGL3EbDU0/wn47KAXLq6xj7xzR1wBJejyzrFITFBtIx6e03jj4AVNIuYkum2gfl0N9BpAbNpJfFf1RGCIJfJgqjMyVT0gPjjzQw9yPIzQhA3QxRCkg5hMoynCjUv9tZANFsJCpPkYAAAAABJRU5ErkJggg==",
          },
          {
            id: REPLACEMENT_MAX_SHARPE,
            z: 3,
            name: "Max sharpe portfolio",
            data: [],
            type: "scatter",
            symbol:
              "image://data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEHSURBVHgBbZC9TsMwFIWPnZAyJFUCYigsXSqYkVirAA9CBiRWxAvQvkAVVkAiPAFdWFGEGHgEfhYW6EjUZGhN44ttqVH/zmDp+nw69j0MMzo/zUPOZQiJTE5kP06Cr6nHDBD9+taa/RBs8bCxzSEEMPiRKIay07v2uhV4cVbctXatqH1cq9KLnPDYH2GYycP41ku5Sms6DqL9A2f2F3A9hvaRA8ZwqWcO225ubHK4dYZFNXYsDYYGtIj82voytChelmU6+C4hxrRk6nsipAZUFWTjEV29vog5KFfLPD8JtS3d69nWB/1NOh9v5KuEk9aebdI/380r3d5NPanqwarCXZnEcZBNvX8KQWm+aVP7aAAAAABJRU5ErkJggg==",
          },
          {
            id: "assets",
            data: [],
            type: "scatter",
            color: WEIGHT_CHART_COLORS,
            colorBy: "data",
          },
          {
            id: "suggestion",
            data: [],
            type: "scatter",
            symbolSize: 14,
            color: LIGHT_GREEN,
            itemStyle: {
              borderColor: DARK_GREEN,
            },
          },
        ],
      },
      legendItems: [
        {
          name: this.$i18n.t("portfolioManager.yourPortfolio"),
          dotColor: ICONS_FILL_COLOR,
        },
        {
          name: "Sharpe ratio portfolio",
          dotColor: LIGHT_PURPLE,
        },
        {
          name: "Minvar portfolio",
          dotColor: LIGHT_ORANGE,
        },
      ],
      isDisplayingRecalculateAlert: false,
    };
  },
  methods: {
    ...mapMutations("portfolioManagerStore", [
      "setPointDetail",
      "setSpecialPortfolio",
    ]),
    formatToPercents,
    generatePortfolios(output, isReplacement) {
      const portfolios = [
        {
          key: `${isReplacement ? "replacement_" : ""}min_volatility`,
          data: [
            [
              output.optimal_values.min_volatility.volatility,
              output.optimal_values.min_volatility.return,
            ],
          ],
        },
        {
          key: `${isReplacement ? "replacement_" : ""}max_sharpe`,
          data: [
            [
              output.optimal_values.max_sharpe.volatility,
              output.optimal_values.max_sharpe.return,
            ],
          ],
        },
      ];
      if (!isReplacement && this.portfolio && this.portfolio.portfolio_output) {
        portfolios.push({
          key: "custom_portfolio",
          data: [
            [
              this.portfolio.portfolio_output.custom_portfolio.volatility,
              this.portfolio.portfolio_output.custom_portfolio.expected_return,
            ],
          ],
        });
      }
      portfolios.forEach((portfolio) => {
        this.chartOptions.series[
          this.chartOptions.series.findIndex((ser) => ser.id === portfolio.key)
        ].data = portfolio.data;
      });
    },
    invokeRecalculateAlert() {
      if (!this.isDisplayingRecalculateAlert) {
        this.isDisplayingRecalculateAlert = true;
        this.$toast.info(
          {
            component: MainAlert,
            props: {
              message: this.$i18n.t("portfolioManager.needRecalculation"),
            },
          },
          {
            onClose: () => (this.isDisplayingRecalculateAlert = false),
            timeout: 3000,
          }
        );
        this.$emit("recalculateMeasures");
      }
    },
    populateRecalculatedFrontier(data) {
      const replacementSeriesIndex = this.chartOptions.series.findIndex(
        (ser) => ser.id === "replacementFrontier"
      );
      const source = data ? data : this.recalculatedPortfolio;
      const output = source.portfolio_output;
      const frontier = output.efficient_frontier;
      const newFrontierData = Object.values(frontier.expected_return).map(
        (val, i) => [Object.values(frontier.volatility)[i], val]
      );
      this.generatePortfolios(output, true);
      this.chartOptions.series[replacementSeriesIndex].name =
        !source.custom_portfolio_data
          ? this.$i18n.t("portfolioManager.replacementFrontierPool")
          : this.$i18n.t("portfolioManager.replacementFrontier");
      this.chartOptions.series[replacementSeriesIndex].color =
        !source.custom_portfolio_data ? PRIMARY_FONT_BLUE : "#779EFF";
      this.chartOptions.series[replacementSeriesIndex].data = newFrontierData;
    },
    populateData(newVal, oldVal) {
      if (
        this.shouldRecalculate &&
        !this.replacePortfolioCoords &&
        newVal &&
        newVal.portfolio_id &&
        (oldVal === null || oldVal.portfolio_id !== newVal.portfolio_id)
      ) {
        this.invokeRecalculateAlert();
      }
      const output =
        this.portfolio &&
        this.portfolio.portfolio_id &&
        this.portfolio.portfolio_output &&
        !this.shouldRecalculate
          ? this.portfolio["portfolio_output"]
          : this.defaultPortfolio.output_portfolio;
      const frontier = output.efficient_frontier;
      if (output && frontier) {
        this.generatePortfolios(output);
        this.chartOptions.series[0].data = Object.values(
          frontier.expected_return
        ).map((val, i) => [Object.values(frontier.volatility)[i], val]);
      }
      if (
        this.portfolio &&
        this.portfolio.portfolio_output?.each_stock_results
      ) {
        const stockResults = this.portfolio.portfolio_output.each_stock_results;

        this.chartOptions.series[7].data =
          this.portfolio.portfolio_input.custom_portfolio.map(({ ticker }) => ({
            value: [
              stockResults.volatility[ticker],
              stockResults.expected_returns[ticker],
            ],
            label: ticker,
          }));
      } else {
        this.chartOptions.series[7].data = [];
        if (!this.portfolio) {
          this.chartOptions.series[2].data = [];
        }
      }
    },
    displaySuggestedOptimalization() {
      this.chartOptions.series[8].data = [
        [
          this.suggestedOptimalization.measures.volatility,
          this.suggestedOptimalization.measures.expected_return,
        ],
      ];
      this.chartOptions.series[8].name =
        this.$i18n.t("portfolioManager.suggestOptimalization.proposal") +
        " - " +
        this.suggestedOptimalization.name +
        this.$i18n.t("portfolioManager.suggestOptimalization.replacement");
      this.legendItems.push({
        value: "Suggested optimization",
        name: this.$i18n.t(
          "portfolioManager.suggestOptimalization.suggestedOptimalization"
        ),
        dotColor: LIGHT_GREEN,
      });
    },
    handleChartClick(e) {
      if (
        e.componentType === "markPoint" ||
        e.componentIndex === 2 ||
        e.componentIndex === 7 ||
        this.isCalculatingFrontier
      )
        return null;

      if (e.componentIndex === 8) {
        this.setPointDetail({
          values: this.suggestedOptimalization.values,
          measures: this.suggestedOptimalization.measures,
          name:
            this.$i18n.t("portfolioManager.suggestOptimalization.proposal") +
            " - " +
            this.suggestedOptimalization.name +
            this.$i18n.t("portfolioManager.suggestOptimalization.replacement"),
        });
        return null;
      }
      const index = e.seriesIndex;
      const mainSeriesIndexes = [0, 1];
      if (mainSeriesIndexes.includes(index)) {
        const target =
          this[
            index === 1
              ? "recalculatedPortfolio"
              : !this.portfolio ||
                !this.portfolio.portfolio_id ||
                this.shouldRecalculate
              ? "defaultPortfolio"
              : "portfolio"
          ];

        const subTarget =
          target[
            index === 1 ||
            (this.portfolio &&
              this.portfolio.portfolio_id &&
              !this.shouldRecalculate)
              ? "portfolio_output"
              : "output_portfolio"
          ];
        const portfolioDetailWeights = filterObject(
          subTarget.all_weights[e.dataIndex],
          (weight) => weight > 0
        );
        const portfolioDetailAmounts = subTarget.all_amounts[e.dataIndex]
          ? target[this.portfolio ? "portfolio_output" : "output_portfolio"]
              .all_amounts[e.dataIndex]
          : null;
        const portfolioDetailCapitals = subTarget.all_capital[e.dataIndex]
          ? subTarget.all_capital[e.dataIndex]
          : null;
        const portfolioDetail = Object.entries(portfolioDetailWeights).map(
          ([key, value]) => ({
            ticker: key,
            weight: value,
            amount: portfolioDetailAmounts ? portfolioDetailAmounts[key] : null,
            capital: portfolioDetailCapitals
              ? portfolioDetailCapitals[key]
              : null,
          })
        );
        const sharpe = subTarget.efficient_frontier.sharpes[e.dataIndex];
        const valueAtRisk_5 =
          subTarget.efficient_frontier.valueAtRisk_5[e.dataIndex];
        this.setPointDetail({
          values: portfolioDetail,
          measures: {
            volatility: e.data[0],
            expected_return: e.data[1],
            sharpe: sharpe,
            valueAtRisk_5: valueAtRisk_5,
          },
        });
        mainSeriesIndexes.forEach((i) => {
          this.chartOptions.series[i].markPoint.data = [];
        });
        this.chartOptions.series[index].markPoint = {
          data: [
            {
              coord: e.data,
            },
          ],
        };
      } else {
        this.chartOptions.series[0].markPoint = {
          data: [],
        };
        const portfolioId = this.chartOptions.series[e.seriesIndex].id;
        const target =
          !this.portfolio ||
          !this.portfolio.portfolio_id ||
          this.shouldRecalculate
            ? "defaultPortfolio"
            : REPLACEMENT_PORTFOLIOS.includes(portfolioId)
            ? "recalculatedPortfolio"
            : "portfolio";
        const subTarget =
          this[target][
            this.portfolio && !this.shouldRecalculate
              ? "portfolio_output"
              : "output_portfolio"
          ];

        const specialPortfolio =
          subTarget.optimal_values[portfolioId.replace("replacement_", "")];
        const specialPortfolioWeights = filterObject(
          specialPortfolio.weights,
          (weight) => weight > 0
        );
        const portfolioDetailAmounts = subTarget.all_amounts[
          specialPortfolio.index
        ]
          ? subTarget.all_amounts[specialPortfolio.index]
          : null;
        const portfolioDetailCapitals = subTarget.all_capital[
          specialPortfolio.index
        ]
          ? subTarget.all_capital[specialPortfolio.index]
          : null;
        const portfolioDetail = Object.entries(specialPortfolioWeights).map(
          ([key, value]) => ({
            ticker: key,
            weight: value,
            amount: portfolioDetailAmounts ? portfolioDetailAmounts[key] : null,
            capital: portfolioDetailCapitals
              ? portfolioDetailCapitals[key]
              : null,
          })
        );
        this.setSpecialPortfolio({
          portfolioId: portfolioId,
          pointDetail: {
            values: portfolioDetail,
            measures: {
              volatility: e.data[0],
              expected_return: e.data[1],
              return: e.data[1],
              sharpe: specialPortfolio.sharpe,
              valueAtRisk_5: specialPortfolio.valueAtRisk_5,
            },
            name: this.chartOptions.series[e.seriesIndex].name,
          },
        });
      }
      this.$nextTick(() => this.$scrollTo(".point-detail"));
    },
  },
  computed: {
    ...mapGetters("portfolioManagerStore", [
      "portfolio",
      "pointDetail",
      "recalculatedPortfolio",
      "replacePortfolioCoords",
      "defaultPortfolio",
      "isCalculatingFrontier",
      "isCalculatingSuggestion",
      "suggestedOptimalization",
      "pools",
      "selectedPool",
    ]),
    shouldRecalculate() {
      return !!(
        this.portfolio &&
        this.portfolio.portfolio_id &&
        !moment(this.portfolio.date).isSame(moment(), "day")
      );
    },
    updatedAt() {
      return !this.shouldRecalculate && this.portfolio && this.portfolio_id
        ? this.portfolio.date
        : this.defaultPortfolio.date;
    },
  },
  watch: {
    portfolio: {
      handler(newVal, oldVal) {
        this.populateData(newVal, oldVal);
      },
      deep: true,
    },
    defaultPortfolio() {
      if (
        !this.portfolio ||
        !this.portfolio.portfolio_id ||
        !this.portfolio.portfolio_output ||
        this.shouldRecalculate
      ) {
        this.populateData();
      }
    },
    suggestedOptimalization(val) {
      if (val !== null && val.changed) {
        this.displaySuggestedOptimalization();
        this.$toast.success({
          component: MainAlert,
          props: {
            message: this.$i18n.t(
              "portfolioManager.suggestOptimalization.alertSuccess",
              {
                result: val.new,
                old: val.old,
              }
            ),
          },
        });
      } else {
        if (val !== null) {
          this.$toast.info({
            component: MainAlert,
            props: {
              message: this.$i18n.t(
                "portfolioManager.suggestOptimalization.alertInfo",
                {
                  result: val.new,
                  old: val.old,
                }
              ),
            },
          });
        }
        this.chartOptions.series[8].data = [];
        const index = this.legendItems.findIndex(
          (item) => item.value === "Suggested optimization"
        );
        if (index !== -1) {
          this.legendItems.splice(index, 1);
        }
      }
    },
    isCalculatingSuggestion(newVal) {
      if (!newVal) {
        this.showSuggestionForm = false;
      }
    },
    recalculatedPortfolio(newPortfolio) {
      const replacementSeriesIndex = this.chartOptions.series.findIndex(
        (ser) => ser.id === "replacementFrontier"
      );
      if (newPortfolio) {
        this.populateRecalculatedFrontier(newPortfolio);
      } else {
        this.chartOptions.series[replacementSeriesIndex].data = [];
        this.chartOptions.series[5].data = [];
        this.chartOptions.series[6].data = [];
      }
    },
    replacePortfolioCoords(newVal) {
      const mainSeriesIndexes = [0, 1];
      mainSeriesIndexes.forEach((i) => {
        this.chartOptions.series[i].markPoint.data = [];
      });
      const cpid = this.chartOptions.series.findIndex(
        (ser) => ser.id === CUSTOM_PORTFOLIO
      );
      this.chartOptions.series[cpid].data = [newVal];
    },
  },
  mounted() {
    this.populateData();
    if (this.recalculatedPortfolio !== null) {
      this.populateRecalculatedFrontier();
    }
  },
};
</script>

<style lang="scss" scoped>
.markowitz-chart-root {
  padding: toRem(15);
  border: 1px solid #f3f3f3;
  @include dps-box;
  position: relative;
  .markowitz-chart {
    width: 80%;
  }
  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .frontier-loader {
      span {
        margin-right: toRem(5);
      }
      margin-bottom: 0.5rem;
      margin-left: 0.5rem;
    }
    .flex {
      width: auto;
    }
  }
  .suggestion-form {
    display: flex;
    align-items: center;
    .tooltip-info {
      margin-left: 5px;
      &.margin-bottom {
        margin-bottom: 0.75rem;
      }
    }
  }
  .chart-footer {
    display: flex;
    justify-content: center;
    flex-direction: column;
    .flex {
      width: 70px;
      align-self: center;
    }
  }
  .chart-with-sides {
    display: flex;
    .chart-left {
      display: flex;
      align-items: center;
      max-width: 50px;
      .title-container {
        margin-top: 6vw;
      }
      .flex {
        transform: rotate(-90deg);
      }
    }
  }
  .legend {
    min-width: 150px;
  }
  .chart-right {
    display: flex;
    align-items: center;
    .frontier-expl {
      display: flex;
      align-items: center;
      .markline {
        width: 15px;
        height: 3px;
        background-color: #002333;
        margin-right: 10px;
        &.repl {
          background-color: #779eff;
          &.pool {
            background-color: $primaryFontBlue;
          }
        }
      }
    }
  }
  .updated-at {
    position: absolute;
    right: 8px;
    bottom: 7px;
    display: flex;
    align-items: center;
    color: $subtleText;
    svg {
      margin-right: 4px;
    }
  }
  .info {
    margin-left: 50px;
    color: $primaryFontBlue;
    &.blue {
      color: #779eff;
    }
  }
}
@media only screen and (max-width: 1250px) {
  .markowitz-chart-root {
    .chart-header {
      &.open {
        flex-direction: column;
        align-items: flex-start;
        .suggestion-form {
          align-self: flex-end;
        }
      }
    }
  }
}
</style>